import React, { useEffect, useRef, useState } from 'react';
import { memberApi } from '../api';
import { useMemberApp } from '../AppProvider';
import { IconBookmark, IconCopy, IconSpeak } from './Icons';

function speak(text) {
  if (!window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const utter = new SpeechSynthesisUtterance(text);
  const preferred = localStorage.getItem('byonco:voice') || 'en-US';
  utter.lang = preferred;
  const voices = window.speechSynthesis.getVoices();
  const match = voices.find((v) => v.lang.startsWith(preferred) || v.lang.startsWith(preferred.slice(0, 2)));
  if (match) utter.voice = match;
  window.speechSynthesis.speak(utter);
}

export default function Thread({ messages, pending }) {
  const { isPro, setUpgradeOpen, requirePro } = useMemberApp();
  const [copied, setCopied] = useState('');
  const [saved, setSaved] = useState({});
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, pending]);

  useEffect(() => {
    let live = true;
    memberApi.bookmarks()
      .then((data) => {
        if (!live) return;
        const next = {};
        (data.bookmarks || []).forEach((row) => {
          if (row?.message_id && row?.id) next[row.message_id] = row.id;
        });
        setSaved(next);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  const bookmark = async (msg) => {
    if (!requirePro()) return;
    const existingId = saved[msg.id];
    if (existingId) {
      setSaved((prev) => {
        const next = { ...prev };
        delete next[msg.id];
        return next;
      });
      try {
        await memberApi.deleteBookmark(existingId);
      } catch (error) {
        setSaved((prev) => ({ ...prev, [msg.id]: existingId }));
        if (error.status === 402) setUpgradeOpen(true);
      }
      return;
    }
    setSaved((prev) => ({ ...prev, [msg.id]: 'pending' }));
    try {
      const created = await memberApi.addBookmark({
        conversation_id: msg.conversation_id,
        message_id: msg.id,
        title: (msg.content || '').slice(0, 80),
        body: msg.content,
      });
      setSaved((prev) => ({ ...prev, [msg.id]: created.id || prev[msg.id] }));
    } catch (error) {
      setSaved((prev) => {
        const next = { ...prev };
        delete next[msg.id];
        return next;
      });
      if (error.status === 402) setUpgradeOpen(true);
    }
  };

  if (!pending && !(messages || []).length) {
    return (
      <div className="by-thread">
        <div className="by-thread-empty">Ask anything that did not fit in the clinic visit.</div>
      </div>
    );
  }

  return (
    <div className="by-thread">
      {(messages || []).map((msg) => (
        <div key={msg.id} className={`by-msg ${msg.role}`}>
          {msg.role === 'user' ? (
            <div className="by-bubble">{msg.content}</div>
          ) : (
            <div>
              <div className="by-answer">{msg.content}</div>
              <div className="by-actions">
                <button type="button" className="by-icon-btn" aria-label="Read aloud" onClick={() => speak(msg.content)}>
                  <IconSpeak />
                </button>
                <button
                  type="button"
                  className="by-icon-btn"
                  aria-label="Copy"
                  onClick={() => {
                    navigator.clipboard?.writeText(msg.content);
                    setCopied(msg.id);
                    setTimeout(() => setCopied(''), 1200);
                  }}
                >
                  <IconCopy />
                </button>
                <button
                  type="button"
                  className={`by-icon-btn${saved[msg.id] ? ' is-saved' : ''}`}
                  aria-label={saved[msg.id] ? 'Remove bookmark' : 'Bookmark'}
                  aria-pressed={!!saved[msg.id]}
                  onClick={() => bookmark(msg)}
                >
                  <IconBookmark filled={!!saved[msg.id]} />
                </button>
                {copied === msg.id ? <span className="by-fine" style={{ margin: 0 }}>Copied</span> : null}
                {!isPro ? <span className="by-pro">PRO</span> : null}
              </div>
            </div>
          )}
        </div>
      ))}
      {pending ? (
        <div className="by-msg assistant">
          <div className="by-thinking" aria-live="polite">
            <span /><span /><span />
          </div>
        </div>
      ) : null}
      <div ref={endRef} />
    </div>
  );
}
